import type {
  GeneratePlanInput,
  DreamPlan,
  PlanGoal,
  PlanTenMinuteAction,
  PlanHabit,
  SevenDayStep,
  PlanObstacle,
  AspirationId,
  ObstacleId,
} from "./types";
import {
  ENGINE_VERSION,
} from "./state";
import {
  OBSTACLES,
} from "./options";
import {
  GOAL_TEMPLATES,
  TEN_MINUTE_ACTIONS,
  HABIT_TEMPLATES,
  SEVEN_DAY_SEQUENCES,
  IMAGINED_VS_CHANGEABLE,
  HONEST_LIMITS,
} from "./banks";
import { fnv1a, mulberry32 } from "@/lib/infiniteSelves/scoring";

/**
 * Determine the 3 default obstacles deterministically if none were chosen.
 */
function pickDefaultObstacles(
  aspirations: AspirationId[],
  timeBudget: GeneratePlanInput["timeBudget"]
): [ObstacleId, ObstacleId, ObstacleId] {
  if (timeBudget === "10min-day" || timeBudget === "1hr-week") {
    return ["no-time", "distraction", "losing-momentum"];
  }
  if (aspirations.includes("craft") || aspirations.includes("voice")) {
    return ["perfectionism", "fear-of-failing", "self-doubt"];
  }
  return ["low-energy", "losing-momentum", "distraction"];
}

/**
 * Generate a complete, pure, deterministic DreamPlan.
 *
 * Requirements:
 * - Pure: identical inputs + same ENGINE_VERSION gives deeply equal output.
 * - No Math.random, no Date.now.
 * - Returns null or throws if aspirations are empty or approvedInterpretation is empty/whitespace.
 * - Always 3 goals spread across chosen aspirations (handling 1, 2, or 3 aspirations cleanly without duplicates).
 * - Exactly one 10-minute action.
 * - Exactly one habit with daily/weekly cadence matching time budget.
 * - Exactly 7 daily steps with minutes respecting the time budget:
 *     - "10min-day": every step <= 10 minutes.
 *     - "30min-day": steps 8..30 minutes.
 *     - "1hr-week": weekly habit, steps short (5-10m) with day 4 or 7 longer (25-30m).
 *     - "3hr-week": steps expanded to accommodate longer focus sessions.
 * - Exactly 3 obstacle responses, tailored where available.
 * - Imagined vs changeable table matching primary aspiration.
 * - Honest limits note.
 * - stable planId based on FNV-1a hash of inputs.
 * - usedFreeText: false (never reads free text).
 */
export function generatePlan(input: GeneratePlanInput): DreamPlan | null {
  const {
    persona,
    themes,
    aspirations,
    approvedInterpretation,
    timeBudget,
    obstacles: userObstacles,
  } = input;

  // Validation
  if (!aspirations || aspirations.length === 0) {
    return null;
  }
  if (!approvedInterpretation || approvedInterpretation.trim().length === 0) {
    return null;
  }
  if (!timeBudget) {
    return null;
  }

  // Deterministic seed
  const personaKey = persona.kind === "self" ? `${persona.archetype}:${persona.name}` : "own";
  const themesKey = [...themes].sort().join(",");
  const aspKey = [...aspirations].join(",");
  const budgetKey = timeBudget;
  const interpHash = fnv1a(approvedInterpretation.trim());
  const seedString = `${ENGINE_VERSION}:${personaKey}:${themesKey}:${aspKey}:${budgetKey}:${interpHash}`;
  const seed = fnv1a(seedString);
  const rng = mulberry32(seed);

  const planId = `plan-${(seed >>> 0).toString(16).slice(0, 8)}`;

  // 1. Generate 3 Goals spread across aspirations
  const primaryAsp = aspirations[0];
  const secondaryAsp = aspirations.length > 1 ? aspirations[1] : primaryAsp;
  const tertiaryAsp = aspirations.length > 2 ? aspirations[2] : (aspirations.length > 1 ? aspirations[0] : primaryAsp);

  let goal0TemplateIndex = 0;
  let goal1TemplateIndex = 0;
  let goal2TemplateIndex = 0;

  if (aspirations.length === 1) {
    // Single aspiration -> use all 3 distinct templates
    goal0TemplateIndex = 0;
    goal1TemplateIndex = 1;
    goal2TemplateIndex = 2;
  } else if (aspirations.length === 2) {
    // 2 aspirations -> [asp0 t0, asp1 t0, asp0 t1]
    goal0TemplateIndex = 0;
    goal1TemplateIndex = 0;
    goal2TemplateIndex = 1;
  } else {
    // 3 aspirations -> [asp0 t0, asp1 t0, asp2 t0]
    goal0TemplateIndex = 0;
    goal1TemplateIndex = 0;
    goal2TemplateIndex = 0;
  }

  const g0 = GOAL_TEMPLATES[primaryAsp][goal0TemplateIndex];
  const g1 = GOAL_TEMPLATES[secondaryAsp][goal1TemplateIndex];
  const g2 = GOAL_TEMPLATES[tertiaryAsp][goal2TemplateIndex];

  const goals: [PlanGoal, PlanGoal, PlanGoal] = [
    {
      id: `${planId}-g1`,
      aspirationId: primaryAsp,
      title: g0.title,
      why: g0.why,
      measure: g0.measure,
    },
    {
      id: `${planId}-g2`,
      aspirationId: secondaryAsp,
      title: g1.title,
      why: g1.why,
      measure: g1.measure,
    },
    {
      id: `${planId}-g3`,
      aspirationId: tertiaryAsp,
      title: g2.title,
      why: g2.why,
      measure: g2.measure,
    },
  ];

  // 2. Ten-minute action from primary aspiration
  const actionList = TEN_MINUTE_ACTIONS[primaryAsp];
  const actionIndex = Math.floor(rng() * actionList.length);
  const tenMinuteAction: PlanTenMinuteAction = actionList[actionIndex];

  // 3. Habit matching time budget cadence
  const habitPair = HABIT_TEMPLATES[primaryAsp];
  const habit: PlanHabit =
    timeBudget === "1hr-week" || timeBudget === "3hr-week"
      ? habitPair[1] // weekly
      : habitPair[0]; // daily

  // 4. Seven-Day Sequence adapted to time budget
  const baseSequence = SEVEN_DAY_SEQUENCES[primaryAsp];
  const sevenDay: SevenDayStep[] = baseSequence.map((step) => {
    let minutes = step.minutes;

    if (timeBudget === "10min-day") {
      // Must be <= 10 min
      minutes = Math.min(10, Math.max(5, Math.round(step.minutes * 0.4)));
      if (minutes > 10) minutes = 10;
    } else if (timeBudget === "30min-day") {
      // Scale to approx 15..30 min
      minutes = Math.min(30, Math.max(10, Math.round(step.minutes * 1.0)));
    } else if (timeBudget === "1hr-week") {
      // 5-10 min on most days, longer 25m on Day 4 and Day 6
      if (step.day === 4 || step.day === 6) {
        minutes = 25;
      } else {
        minutes = Math.min(8, Math.max(5, Math.round(step.minutes * 0.3)));
      }
    } else if (timeBudget === "3hr-week") {
      // 3 hours weekly: 30-45m sessions on day 3, 5, 6
      if (step.day === 3 || step.day === 5 || step.day === 6) {
        minutes = 45;
      } else {
        minutes = Math.min(15, Math.max(8, step.minutes));
      }
    }

    return {
      day: step.day,
      title: step.title,
      task: step.task,
      minutes,
    };
  });

  // 5. Obstacles (top 3)
  let obstacleIds: ObstacleId[] = [];
  if (userObstacles && userObstacles.length > 0) {
    obstacleIds = [...userObstacles].slice(0, 3);
  } else {
    obstacleIds = pickDefaultObstacles(aspirations, timeBudget);
  }

  // Ensure exactly 3
  while (obstacleIds.length < 3) {
    const fallbackIds: ObstacleId[] = ["no-time", "distraction", "losing-momentum", "self-doubt"];
    for (const f of fallbackIds) {
      if (!obstacleIds.includes(f)) {
        obstacleIds.push(f);
        if (obstacleIds.length === 3) break;
      }
    }
  }

  const obstacles: PlanObstacle[] = obstacleIds.slice(0, 3).map((id) => {
    const opt = OBSTACLES.find((o) => o.id === id)!;
    const responseLines: string[] = [opt.generalResponses[0], opt.generalResponses[1]];

    // If there is an aspiration-specific variant for this aspiration, weave it in
    if (opt.aspirationResponses && opt.aspirationResponses[primaryAsp]) {
      responseLines[1] = opt.aspirationResponses[primaryAsp]!;
    }

    return {
      id,
      label: opt.label,
      response: responseLines,
    };
  });

  // 6. Imagined vs Changeable
  const imaginedVsChangeable = IMAGINED_VS_CHANGEABLE[primaryAsp];

  // 7. Honest limits sentence (seeded pick)
  const limitIndex = Math.floor(rng() * HONEST_LIMITS.length);
  const honestLimit = HONEST_LIMITS[limitIndex];

  return {
    engineVersion: ENGINE_VERSION,
    planId,
    goals,
    tenMinuteAction,
    habit,
    sevenDay,
    obstacles,
    imaginedVsChangeable,
    honestLimit,
    usedFreeText: false,
  };
}
